import { DynamicModelType } from '../models';

/**
 * Control types whose saved id property is fixed by their data source, so neither a missing
 * valueProperty nor a stored one may override it (WI 34964). `typeahead-vendor` records are
 * `{ vendor, name, vendorGroup }` from the Tool Tracker vendor search; the answer is the vendor
 * number. `typeahead-gl-account` records are `{ glAcct, description, companyID, name }` from the
 * Viewpoint GL account search; the answer is the account number (WI 34966).
 * `typeahead-purchase-order` records come from the Viewpoint PO search; the answer is the PO
 * number (WI 34963). `typeahead-category` serves hard-coded `{ Id, ... }` records (its base component), so
 * it is always 'Id'; this map replaced the stored-form switch case that set it.
 */
const DYNAMIC_PINNED_VALUE_PROPERTY: Partial<Record<DynamicModelType, string>> =
  {
    'typeahead-category': 'Id',
    'typeahead-vendor': 'vendor',
    'typeahead-gl-account': 'glAcct',
    'typeahead-purchase-order': 'po',
  };

/** The valueProperty a control saves by: the type's pinned property, else the stored
 *  valueProperty, else the formControlName (the long-standing default). */
export function dynamicValuePropertyFor(model: {
  type?: DynamicModelType;
  valueProperty?: string;
  formControlName?: string;
}): string | undefined {
  return (
    (model.type && DYNAMIC_PINNED_VALUE_PROPERTY[model.type]) ||
    model.valueProperty ||
    model.formControlName
  );
}

/**
 * True when a control's valueProperty names a real id property: pinned by its type, or
 * configured on the stored form. False when it is only the formControlName default that
 * dynamicValuePropertyFor falls back to, which says nothing about the record's shape
 * (Bug 35153: treating that default as an id blocked Submit on filled typeaheads).
 */
export function dynamicValuePropertyIsExplicit(model: {
  type?: DynamicModelType;
  valueProperty?: string;
  formControlName?: string;
}): boolean {
  if (model.type && DYNAMIC_PINNED_VALUE_PROPERTY[model.type]) {
    return true;
  }
  return !!model.valueProperty && model.valueProperty !== model.formControlName;
}

/**
 * The property a picked record's id is read from (Bug 35174): the configured valueProperty
 * when the record carries it, else Id or id, but only where those are the id by construction.
 * That is a control mapped through options.api.model (apiModelMapper rebuilds each record as
 * { Id, id, Name, ... } and drops the raw keys) or one whose valueProperty is only the
 * formControlName default. An explicit key on a raw record and a pinned type never fall back:
 * a raw record's Id may be another identifier, and a wrong id is worse than a visible NULL.
 */
export function dynamicRecordIdProperty(
  record: unknown,
  model: {
    type?: DynamicModelType;
    valueProperty?: string;
    formControlName?: string;
    options?: { api?: { model?: unknown } };
  }
): string | undefined {
  const configured = model.valueProperty;
  if (record == null || typeof record !== 'object') {
    return configured;
  }
  const has = (key: string) =>
    Object.prototype.hasOwnProperty.call(record, key);
  if (configured && has(configured)) {
    return configured;
  }
  const pinned = !!(model.type && DYNAMIC_PINNED_VALUE_PROPERTY[model.type]);
  const idIsById =
    !!model.options?.api?.model || !dynamicValuePropertyIsExplicit(model);
  if (pinned || !idIsById) {
    return configured;
  }
  return ['Id', 'id'].find(has) ?? configured;
}
